import { uniqBy } from 'lodash';
import { gql } from '../gql';
import type { FragmentOf } from '../gql';
import type { Comment } from 'schema-dts';

const COMMENT_SCHEMA_BASE_FRAGMENT = gql(`
  fragment CommentSchemaBaseFields on Comment @_unmask {
    id
    parent {
      id
    }
    createdAt
    updatedAt
    excerpt
    owner {
      displayName
      publicId
      imageUrl
    }
  }
`);

export const COMMENT_SCHEMA_FRAGMENT = gql(
  `
  fragment CommentSchemaFields on Comment @_unmask {
    ...CommentSchemaBaseFields
    ancestors {
      ...CommentSchemaBaseFields
    }
  }
`,
  [COMMENT_SCHEMA_BASE_FRAGMENT]
);

type CommentBaseGql = FragmentOf<typeof COMMENT_SCHEMA_BASE_FRAGMENT>;
type CommentGql = FragmentOf<typeof COMMENT_SCHEMA_FRAGMENT>;
type CommentGqlWithChildren = CommentBaseGql & {
  children?: CommentBaseGql[];
};

const transformCommentToSchema = (comment: CommentGqlWithChildren): Comment => {
  const displayName = comment.owner?.displayName;
  const publicId = comment.owner?.publicId;
  const imageUrl = comment.owner?.imageUrl;
  const commentSchema = {
    '@type': 'Comment',
    author: {
      '@type': 'Person',
      '@id': publicId
        ? `https://simplywall.st/community/users/${publicId}`
        : undefined,
      name: displayName || undefined,
      identifier: publicId || undefined,
      image: imageUrl || undefined,
    },
    text: comment.excerpt,
    dateCreated: new Date(comment.createdAt).toISOString(),
    dateModified: new Date(comment.updatedAt).toISOString(),
    comment: comment.children?.length
      ? comment.children.map((child) => transformCommentToSchema(child))
      : undefined,
  } satisfies Comment;

  const author = commentSchema.author;
  if (!commentSchema.comment) delete commentSchema.comment;
  if (!author['@id']) delete author['@id'];
  if (!author.name) delete author.name;
  if (!author.image) delete author.image;
  if (!author.identifier) delete author.identifier;

  return commentSchema;
};

const createCommentTree = (
  comments: CommentBaseGql[],
  parentId: string | null
): CommentGqlWithChildren[] => {
  const children = comments.filter((c) => (c.parent?.id ?? null) === parentId);

  return children.map((child) => ({
    ...child,
    children: createCommentTree(comments, child.id),
  }));
};

export const getCommentSchema = (comments: CommentGql[]): Comment[] => {
  const allComments = uniqBy(
    comments.flatMap((comment) => [comment, ...comment.ancestors]),
    'id'
  );
  const commentsAsTree = createCommentTree(allComments, null);

  return commentsAsTree.map(transformCommentToSchema);
};
